import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Card, { CardHeader } from '../components/Card';
import DataTable, { Column } from '../components/DataTable';
import Badge, { statusToTone } from '../components/Badge';
import DonutChart, { DonutSlice } from '../components/DonutChart';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';
import { faCartShopping,  faCreditCard, faIndianRupeeSign, faMagnifyingGlassChart,  faUsers } from '@fortawesome/free-solid-svg-icons';

interface DashboardStats {
  total_users: number;
  active_subscriptions: number;
  total_checks_this_month: number;
  today_sales: number;
  monthly_revenue: number;
  users_trend: string;
  revenue_trend: string;
  today_sales_trend: string;
  users_positive: boolean;
  revenue_positive: boolean;
  today_sales_positive: boolean;
}

/** Raw shape returned by GET /api/admin/dashboard-cards/ */
interface DashboardCardsRaw {
  orders_today: { value: number; change_percent: number; compare_label: string };
  monthly_sales: { value: number; change_percent: number; compare_label: string };
  total_users: { value: number; change_percent: number; compare_label: string };
  active_subscriptions: { value: number };
  checks_this_month: { value: number; ai_checks: number; plagiarism_checks: number };
}

/** Raw shape returned by GET /api/admin/monthly-sales/ */
interface MonthlySalesRaw {
  this_month_total: number;
  percent_change_vs_last_month: number;
  monthly: { month: string; this_year: number; last_year: number }[];
}

/** Raw shape returned by GET /api/admin/monthly-checks/ */
interface MonthlyChecksRaw {
  total_ai_checks: number;
  total_plagiarism_checks: number;
  monthly: { month: string; ai_check: number; plagiarism_check: number }[];
}

/** Raw shape returned by GET /api/admin/total-subscriptions/ */
interface TotalSubscriptionsRaw {
  total: number;
  split_by_plan: { plan_name: string; total_count: number }[];
}

/** Raw shape of a single row returned by GET /api/admin/recent-activity/ */
interface RecentActivityRaw {
  s_no: number;
  user_account: string;
  activity: string;
  status: string;
  date: string;
}

interface ActivityRow {
  id: string;
  user: string;
  action: string;
  status: string;
  date: string;
}

interface MonthlySales {
  month: string;
  last_year: number;
  this_year: number;
}

interface CheckBreakdown {
  label: string;
  ai_checks: number;
  plagiarism_checks: number;
}

interface SubscriptionSlice {
  label: string;
  value: number;
}

interface CustomerBase {
  total_registered: number;
  subscribed: number;
  non_subscribed: number;
}

const FALLBACK_STATS: DashboardStats = {
  total_users: 998,
  active_subscriptions: 342,
  total_checks_this_month: 5921,
  today_sales: 50,
  monthly_revenue: 418600,
  users_trend: '+8.2% this month',
  revenue_trend: '+12.4% vs last month',
  today_sales_trend: '+6.4% vs yesterday',
  users_positive: true,
  revenue_positive: true,
  today_sales_positive: true,
};

const FALLBACK_ACTIVITY: ActivityRow[] = [
  { id: '1', user: 'Ritika Sharma', action: 'Plagiarism check submitted', status: 'complete', date: '2026-07-13' },
  { id: '2', user: 'Global Press Ltd', action: 'Upgraded to Institution plan', status: 'active', date: '2026-07-13' },
  { id: '3', user: 'Arjun Mehta', action: 'AI content check submitted', status: 'processing', date: '2026-07-12' },
  { id: '4', user: 'Delta University', action: 'Support ticket opened', status: 'pending', date: '2026-07-12' },
  { id: '5', user: 'Naveen Kumar', action: 'Subscription payment failed', status: 'failed', date: '2026-07-11' },
];

// Calendar-year comparison, Jan through Dec, this year vs last year
const FALLBACK_MONTHLY_SALES: MonthlySales[] = [
  { month: 'Jan', last_year: 298000, this_year: 315600 },
  { month: 'Feb', last_year: 305000, this_year: 331200 },
  { month: 'Mar', last_year: 312000, this_year: 344800 },
  { month: 'Apr', last_year: 320000, this_year: 356900 },
  { month: 'May', last_year: 335000, this_year: 368100 },
  { month: 'Jun', last_year: 341000, this_year: 372400 },
  { month: 'Jul', last_year: 358000, this_year: 418600 },
  { month: 'Aug', last_year: 349000, this_year: 402000 },
  { month: 'Sep', last_year: 362000, this_year: 415000 },
  { month: 'Oct', last_year: 371000, this_year: 428000 },
  { month: 'Nov', last_year: 388000, this_year: 441000 },
  { month: 'Dec', last_year: 402000, this_year: 456000 },
];

const FALLBACK_CHECK_BREAKDOWN: CheckBreakdown[] = [
  { label: 'Aug \'25', ai_checks: 2350, plagiarism_checks: 1890 },
  { label: 'Sep \'25', ai_checks: 2480, plagiarism_checks: 1950 },
  { label: 'Oct \'25', ai_checks: 2610, plagiarism_checks: 2040 },
  { label: 'Nov \'25', ai_checks: 2790, plagiarism_checks: 2130 },
  { label: 'Dec \'25', ai_checks: 2950, plagiarism_checks: 2280 },
  { label: 'Jan \'26', ai_checks: 2830, plagiarism_checks: 2190 },
  { label: 'Feb \'26', ai_checks: 3020, plagiarism_checks: 2260 },
  { label: 'Mar \'26', ai_checks: 3180, plagiarism_checks: 2340 },
  { label: 'Apr \'26', ai_checks: 3310, plagiarism_checks: 2410 },
  { label: 'May \'26', ai_checks: 3400, plagiarism_checks: 2460 },
  { label: 'Jun \'26', ai_checks: 3450, plagiarism_checks: 2480 },
  { label: 'Jul \'26', ai_checks: 3550, plagiarism_checks: 2371 },
];

const FALLBACK_SUB_BREAKDOWN: SubscriptionSlice[] = [
  { label: 'Starter', value: 210 },
  { label: 'Professional', value: 98 },
  { label: 'Institution', value: 27 },
  { label: 'Publisher Pro', value: 7 },
];

const FALLBACK_CUSTOMER_BASE: CustomerBase = {
  total_registered: 998,
  subscribed: 805,
  non_subscribed: 193,
};

const SUB_COLORS = ['#003c6c', '#2563eb', '#38bdf8', '#93c5fd'];

// Raw-shaped fallbacks — mirror exactly what the live endpoints return, so
// they can be fed straight into the same transform functions used for
// real responses (see load() below).
const FALLBACK_CARDS_RAW: DashboardCardsRaw = {
  orders_today: { value: 50, change_percent: 6.4, compare_label: 'vs yesterday' },
  monthly_sales: { value: 418600, change_percent: 12.4, compare_label: 'vs last month' },
  total_users: { value: 998, change_percent: 8.2, compare_label: 'this month' },
  active_subscriptions: { value: 342 },
  checks_this_month: { value: 5921, ai_checks: 3550, plagiarism_checks: 2371 },
};

const FALLBACK_MONTHLY_CHECKS_RAW: MonthlyChecksRaw = {
  total_ai_checks: FALLBACK_CHECK_BREAKDOWN.reduce((s, c) => s + c.ai_checks, 0),
  total_plagiarism_checks: FALLBACK_CHECK_BREAKDOWN.reduce((s, c) => s + c.plagiarism_checks, 0),
  monthly: FALLBACK_CHECK_BREAKDOWN.map(c => ({ month: c.label, ai_check: c.ai_checks, plagiarism_check: c.plagiarism_checks })),
};

const FALLBACK_TOTAL_SUBS_RAW: TotalSubscriptionsRaw = {
  total: FALLBACK_SUB_BREAKDOWN.reduce((s, b) => s + b.value, 0),
  split_by_plan: FALLBACK_SUB_BREAKDOWN.map(b => ({ plan_name: b.label, total_count: b.value })),
};

const FALLBACK_ACTIVITY_RAW: RecentActivityRaw[] = FALLBACK_ACTIVITY.map((a, i) => ({
  s_no: i + 1,
  user_account: a.user,
  activity: a.action,
  status: a.status,
  date: a.date,
}));

// function formatDate(iso: string) {
//   return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
// }

function formatCurrency(n: number) {
  return '₹' + n.toLocaleString('en-IN');
}

function formatTrend(changePercent: number, compareLabel: string) {
  const sign = changePercent >= 0 ? '+' : '';
  return `${sign}${changePercent}% ${compareLabel}`;
}

/**
 * All real admin endpoints (dashboard-cards, monthly-sales, monthly-checks,
 * total-subscriptions, recent-activity) respond with { success, data }.
 * This unwraps `data` and falls back to local sample data if the request
 * fails (network error, 404, backend not deployed yet, etc.).
 */
async function fetchAdmin<T>(path: string, fallback: T): Promise<T> {
  try {
    const res = await axios.get(BaseUrl + path);
    return (res?.data?.data ?? fallback) as T;
  } catch {
    return fallback;
  }
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>(FALLBACK_STATS);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [monthlySales, setMonthlySales] = useState<MonthlySales[]>(FALLBACK_MONTHLY_SALES);
  const [checkBreakdown, setCheckBreakdown] = useState<CheckBreakdown[]>(FALLBACK_CHECK_BREAKDOWN);
  const [subBreakdown, setSubBreakdown] = useState<SubscriptionSlice[]>(FALLBACK_SUB_BREAKDOWN);
  const [customerBase, setCustomerBase] = useState<CustomerBase>(FALLBACK_CUSTOMER_BASE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [cards, sales, checks, subs, activityRaw] = await Promise.all([
        fetchAdmin<DashboardCardsRaw>('admin/dashboard-cards/', FALLBACK_CARDS_RAW),
        fetchAdmin<MonthlySalesRaw>('admin/monthly-sales/', { this_month_total: 0, percent_change_vs_last_month: 0, monthly: FALLBACK_MONTHLY_SALES }),
        fetchAdmin<MonthlyChecksRaw>('admin/monthly-checks/', FALLBACK_MONTHLY_CHECKS_RAW),
        fetchAdmin<TotalSubscriptionsRaw>('admin/total-subscriptions/', FALLBACK_TOTAL_SUBS_RAW),
        fetchAdmin<RecentActivityRaw[]>('admin/recent-activity/', FALLBACK_ACTIVITY_RAW),
      ]);

      setStats({
        total_users: cards.total_users.value,
        active_subscriptions: cards.active_subscriptions.value,
        total_checks_this_month: cards.checks_this_month.value,
        today_sales: cards.orders_today.value,
        monthly_revenue: cards.monthly_sales.value,
        users_trend: formatTrend(cards.total_users.change_percent, cards.total_users.compare_label),
        revenue_trend: formatTrend(cards.monthly_sales.change_percent, cards.monthly_sales.compare_label),
        today_sales_trend: formatTrend(cards.orders_today.change_percent, cards.orders_today.compare_label),
        users_positive: cards.total_users.change_percent >= 0,
        revenue_positive: cards.monthly_sales.change_percent >= 0,
        today_sales_positive: cards.orders_today.change_percent >= 0,
      });

      setMonthlySales(Array.isArray(sales.monthly) ? sales.monthly : FALLBACK_MONTHLY_SALES);

      setCheckBreakdown(
        Array.isArray(checks.monthly)
          ? checks.monthly.map(m => ({ label: m.month, ai_checks: m.ai_check, plagiarism_checks: m.plagiarism_check }))
          : FALLBACK_CHECK_BREAKDOWN
      );

      setSubBreakdown(
        Array.isArray(subs.split_by_plan)
          ? subs.split_by_plan.map(p => ({ label: p.plan_name, value: p.total_count }))
          : FALLBACK_SUB_BREAKDOWN
      );

      // No dedicated "customer base" endpoint yet — derive a reasonable
      // approximation from the totals the dashboard-cards endpoint already
      // gives us (total registered users vs. those with an active subscription).
      setCustomerBase({
        total_registered: cards.total_users.value,
        subscribed: cards.active_subscriptions.value,
        non_subscribed: Math.max(cards.total_users.value - cards.active_subscriptions.value, 0),
      });

      setActivity(
        Array.isArray(activityRaw)
          ? activityRaw.map(a => ({
              id: String(a.s_no),
              user: a.user_account,
              action: a.activity,
              status: a.status,
              date: a.date,
            }))
          : FALLBACK_ACTIVITY
      );

      setLoading(false);
    }
    load();
  }, []);

  const columns: Column<ActivityRow>[] = [
    { key: 'user', header: 'User / Account', render: r => <span className="font-bold text-gray-900">{r.user}</span> },
    { key: 'action', header: 'Activity', render: r => r.action },
    { key: 'status', header: 'Status', render: r => <Badge tone={statusToTone(r.status)}>{r.status}</Badge> },
    { key: 'date', header: 'Date', render: r => r.date, align: 'right' },
  ];

  const totalSubscriptions = subBreakdown.reduce((sum, s) => sum + s.value, 0);
  const subSlices: DonutSlice[] = subBreakdown.map((s, i) => ({ ...s, color: SUB_COLORS[i % SUB_COLORS.length] }));

  const subscribedPct = customerBase.total_registered
    ? Math.round((customerBase.subscribed / customerBase.total_registered) * 100)
    : 0;
  const customerBaseSlices: DonutSlice[] = [
    { label: 'Subscribed', value: customerBase.subscribed, color: '#0b2545' },
    { label: 'Non-subscribed', value: customerBase.non_subscribed, color: '#e5e9f0' },
  ];
  // const arpu = stats.active_subscriptions > 0
  //   ? stats.monthly_revenue / stats.active_subscriptions
  //   : 0;

  const totalAiChecks = checkBreakdown.reduce((sum, c) => sum + c.ai_checks, 0);
  const totalPlagiarismChecks = checkBreakdown.reduce((sum, c) => sum + c.plagiarism_checks, 0);

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle="A snapshot of platform activity, users, and revenue."
        // action={
        //   <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400 bg-gray-50 border border-gray-100 rounded-full px-3 py-1.5">
        //     <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        //     Live data
        //   </div>
        // }
      />

<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
  <StatCard
    label="Orders Today"
    value={stats.today_sales}
    icon={faCartShopping}
    iconBg="#f0fdf4"
    iconColor="#16a34a"
    trend={{ value: stats.today_sales_trend, positive: stats.today_sales_positive }}
  />
  <StatCard
    label="Monthly Sales"
    value={formatCurrency(stats.monthly_revenue)}
    icon={faIndianRupeeSign}
    iconBg="#fffbeb"
    iconColor="#d97706"
    trend={{ value: stats.revenue_trend, positive: stats.revenue_positive }}
  />
  <StatCard
    label="Total Users"
    value={stats.total_users.toLocaleString()}
    icon={faUsers}
    iconBg="#eff6ff"
    iconColor="#2563eb"
    trend={{ value: stats.users_trend, positive: stats.users_positive }}
  />
  <StatCard
    label="Active Subscriptions"
    value={stats.active_subscriptions.toLocaleString()}
    icon={faCreditCard}
    iconBg="#f5f3ff"
    iconColor="#003c6c"
  />
  <StatCard
    label="Checks This Month"
    value={stats.total_checks_this_month.toLocaleString()}
    icon={faMagnifyingGlassChart}
    iconBg="#ecfeff"
    iconColor="#0891b2"
  />

</div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6 items-stretch">
        {/* Monthly sales trend — this year vs last year, Jan through Dec */}
        <Card className="xl:col-span-2 flex flex-col">
          <CardHeader
            title="Monthly Sales"
            subtitle={`${formatCurrency(stats.monthly_revenue)} this month · this year vs last year`}
          />
          <div style={{ width: '100%', height: 300, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlySales} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 600 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => `₹${(v / 1000).toFixed(0)}k`}
                  width={48}
                />
                <Tooltip
                  formatter={(v: any) => formatCurrency(Number(v))}
                  contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} iconType="circle" iconSize={8} />
                <Line
                  type="monotone"
                  dataKey="last_year"
                  name="Last year"
                  stroke="#f97316"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#f97316', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="this_year"
                  name="This year"
                  stroke="#003c6c"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#003c6c', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Total Subscriptions — donut */}
        <Card className="flex flex-col">
          <CardHeader title="Total Subscriptions" subtitle="Split by plan" />
          <div className="flex-1 flex flex-col items-center justify-center py-2">
            <DonutChart data={subSlices} size={150} thickness={18} centerValue={totalSubscriptions} centerLabel="TOTAL" />
            <div className="w-full mt-5 flex flex-col gap-2.5">
              {subSlices.map(s => (
                <div key={s.label} className="flex items-center justify-between text-[12px]">
                  <span className="flex items-center gap-2 font-semibold text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    {s.label}
                  </span>
                  <span className="font-black text-gray-900">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* AI Check vs Plagiarism Check — grouped bar, last 12 months */}
  

      {/* Customer Base — subscription breakdown, matches the sample "81% SUB'D" widget */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6 items-stretch">
          <Card className='xl:col-span-2 flex flex-col'>
          <CardHeader
            title="AI Check vs Plagiarism Check"
            subtitle={`${totalAiChecks.toLocaleString()} AI checks · ${totalPlagiarismChecks.toLocaleString()} plagiarism checks over the trailing 12 months`}
          />
          <div style={{ width: '100%', height: 320, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={checkBreakdown} margin={{ top: 4, right: 8, left: 0, bottom: 8 }} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 600 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} iconType="circle" iconSize={8} />
                <Bar dataKey="ai_checks" name="AI Check" fill="#0891b2" radius={[6, 6, 0, 0]} maxBarSize={28} />
                <Bar dataKey="plagiarism_checks" name="Plagiarism Check" fill="#003c6c" radius={[6, 6, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="flex flex-col ">
          <CardHeader title="Customer Base" subtitle="Subscription breakdown" />
          <div className="flex-1 flex items-center gap-6">
            <DonutChart
              data={customerBaseSlices}
              size={128}
              thickness={16}
              centerValue={`${subscribedPct}%`}
              centerLabel="SUB'D"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[24px] font-black text-gray-900 tracking-tight leading-none">
                {customerBase.total_registered.toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-500 font-semibold mb-4">Total registered users</p>

              <div className="mb-2.5">
                <div className="flex items-center justify-between text-[11px] font-bold mb-1">
                  <span className="text-gray-500">Subscribed</span>
                  <span className="text-gray-900">{customerBase.subscribed}</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${subscribedPct}%`, background: '#0b2545' }} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between text-[11px] font-bold mb-1">
                  <span className="text-gray-500">Non-subscribed</span>
                  <span className="text-gray-900">{customerBase.non_subscribed}</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${100 - subscribedPct}%`, background: '#cbd5e1' }} />
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="xl:col-span-2 flex flex-col">
          <CardHeader title="Recent Activity" subtitle="Latest events across the platform" />
          <DataTable columns={columns} rows={activity} loading={loading} emptyTitle="No recent activity" />
        </Card>
      </div>
    </div>
  );
}